import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Text } from '@atlaskit/primitives';
import Avatar from '@atlaskit/avatar';
import classNames from 'classnames';
import {
  CustomPanel,
  FormattedAddress,
  FormattedDate,
  SummaryPanelContainer,
  SummaryPanelIcon,
  SummaryPanelRow,
  SupportTicketNumber,
} from '@css-xp/base';
import { SourceSystem, Order, OrderBillTo, OrderShipTo } from '@css-xp/graphtypes/src/generated-types-new';
import { BillingContactAddress } from '@atlassian/cssxp-types-billing';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { NoData } from '@css-xp/ppc-common';
import { TaxComponent } from '@css-xp/ppc-tax-component';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths } from '@css-xp/ppc-constants';
import { OrderInfoSection } from './order-info-section';
import './order-details-summary-section.scss';

function getFormattedAddress(address: BillingContactAddress, shouldHideOrgName?: boolean) {
  return (
    <FormattedAddress
      addressLine1={address?.address1}
      addressLine2={address?.address2}
      city={address?.city}
      country={address?.country}
      postcode={address?.postalCode}
      state={address?.state}
      organizationName={shouldHideOrgName ? null : address?.companyName}
    />
  );
}

export const OrderDetailsSummarySection = ({
  order,
  txaId,
  testId = 'order-details-summary-section',
  isVersion2 = false,
  isManualBillingSupportEnabled = false,
  orderHasManuallyBilledItems = false,
}: {
  order: Order;
  txaId: string;
  testId?: string;
  isVersion2?: boolean;
  isManualBillingSupportEnabled?: boolean;
  orderHasManuallyBilledItems?: boolean;
}) => {
  const getBillingContactAddressFromBillTo = (billTo: OrderBillTo | null | undefined) => {
    const address: BillingContactAddress = {
      companyName: billTo?.billToCompanyName,
      address1: billTo?.billToAddress1 || null,
      address2: billTo?.billToAddress2 || null,
      city: billTo?.billToCity || null,
      country: billTo?.billToCountry || null,
      postalCode: billTo?.billToPostalCode || null,
      state: billTo?.billToState || null,
    };
    return address;
  };

  const getShippingContactAddressFromShipTo = (shipTo: OrderShipTo | null | undefined) => {
    const address: BillingContactAddress = {
      companyName: shipTo?.shipToCompanyName,
      address1: shipTo?.shipToAddress1 || null,
      address2: shipTo?.shipToAddress2 || null,
      city: shipTo?.shipToCity || null,
      country: shipTo?.shipToCountry || null,
      postalCode: shipTo?.shipToPostalCode || null,
      state: shipTo?.shipToState || null,
    };
    return address;
  };

  const getAddress = (address: BillingContactAddress, label: string, testId?: string, isVersion2?: boolean) => {
    return (
      <SummaryPanelRow
        label={label}
        testId={testId}
        value={
          <div
            className={classNames('customer-address-section', {
              'order-details-summary-address-section-v2': isVersion2,
            })}
          >
            {isVersion2 && <Text weight="bold">{address?.companyName}</Text>}
            <span
              className={classNames('line-clamp', {
                'mt-8': address?.companyName && isVersion2,
              })}
            >
              {address?.address1 ? getFormattedAddress(address, isVersion2) : <NoData />}
            </span>
            <TaxComponent billToAddress={address} gridItemClassName="tax-component-section" hasSeparator={true} />
          </div>
        }
        useWrapper={isVersion2}
      />
    );
  };

  const getAuthorInfo = () => {
    const { authorProfile, contextSubject } = order;
    if (!authorProfile) return contextSubject;
    return (
      <div className="order-author-container">
        <Avatar src={authorProfile?.picture as string} size="medium" />
        <div className="order-author-name">
          <span>{authorProfile?.name}</span>
          <span className="order-author-email">{authorProfile?.email}</span>
        </div>
      </div>
    );
  };

  const renderBillingProfileInfo = (isVersion2?: boolean) => {
    return (
      <SummaryPanelRow
        label="Billing profile"
        value={
          order?.invoiceGroup?.id ? (
            <>
              <Link
                to={`${getRouteUrl(routePaths.billingProfileDetails, {
                  txaId: txaId,
                  billingProfileId: `${encodeURIComponent(order?.invoiceGroup?.id)}`,
                })}`}
                onClick={(e) => e.stopPropagation()}
                className="billing-profile-name"
                data-testid={`billing-profile-link-${order?.invoiceGroup?.id}`}
              >
                {order?.invoiceGroup?.name || 'Unnamed billing profile'}
              </Link>
            </>
          ) : (
            <NoData testId="billing-id-not-found" />
          )
        }
        useWrapper={isVersion2}
      />
    );
  };

  const renderAuthorInfo = () => {
    return <SummaryPanelRow label="Created by" value={getAuthorInfo()} useWrapper={isVersion2} />;
  };

  const renderCurrency = () => {
    return (
      <SummaryPanelRow
        label="Currency"
        value={order?.invoiceGroup?.currency ? order?.invoiceGroup?.currency : <NoData />}
        useWrapper={isVersion2}
        testId="order-details-summary-section-currency"
      />
    );
  };

  const renderSupportTicketNumber = (isVersion2?: boolean) => {
    const supportTicket = order?.metadata?.supportTicketNumber;

    return (
      <SummaryPanelRow
        label="Support ticket"
        value={supportTicket ? <SupportTicketNumber supportTicket={supportTicket} /> : <NoData />}
        useWrapper={isVersion2}
        isEllipsisRequired
      />
    );
  };

  const renderNote = () => {
    const notes = order?.metadataObj?.handNote;

    return (
      <SummaryPanelRow label="Notes" value={notes ? <p className="order-notes">{notes}</p> : <NoData />} useWrapper />
    );
  };

  const renderContractId = (isVersion2?: boolean) => {
    const contractId = order?.metadataObj?.contractId;
    const contractType = order?.metadataObj?.contractType;
    if (!orderHasManuallyBilledItems) {
      return null;
    }
    return (
      <>
        <SummaryPanelRow label={'Contract ID'} value={contractId ?? <NoData />} useWrapper={isVersion2} />
        <SummaryPanelRow
          label={'Contract type'}
          value={contractType?.replace('_', ' ') ?? <NoData />}
          useWrapper={isVersion2}
        />
      </>
    );
  };

  if (isVersion2) {
    return (
      <>
        <OrderInfoSection order={order} txaId={txaId} />
        <br />
        <div data-testId="order-details-summary-section-v2" className="order-details-summary-section-v2">
          <CustomPanel expandedHeader="Order details" collapsedHeader="Order details" version={2} isExpanded>
            <Box padding="space.300" paddingBlockStart="space.150">
              <SummaryPanelContainer version={2}>
                {renderAuthorInfo()}
                {renderBillingProfileInfo(isVersion2)}
                {renderCurrency()}
                {getAddress(
                  getShippingContactAddressFromShipTo(order?.shipTo),
                  'Sold-to address',
                  'order-summary-ship-to',
                  isVersion2
                )}
                {getAddress(
                  getBillingContactAddressFromBillTo(order?.billTo),
                  'Billing address',
                  'order-summary-bill-to',
                  isVersion2
                )}
                {renderSupportTicketNumber(isVersion2)}
                {renderNote()}
                {isManualBillingSupportEnabled && renderContractId(isVersion2)}
              </SummaryPanelContainer>
            </Box>
          </CustomPanel>
        </div>
      </>
    );
  }

  return (
    <div className="ppc-order-details-summary-section" data-testid={testId}>
      <SummaryPanelContainer title="Order details" iconKey={SummaryPanelIcon.SUBSCRIPTION_ICON}>
        {getAddress(getBillingContactAddressFromBillTo(order?.billTo), 'Billing address', 'order-summary-bill-to')}
        {isManualBillingSupportEnabled && renderContractId()}
      </SummaryPanelContainer>
      <SummaryPanelContainer title="Billing and payment details" iconKey={SummaryPanelIcon.BILLING_PAYMENT_ICON}>
        <SummaryPanelRow
          label="Order date"
          value={<FormattedDate date={order?.created} showTime={true} format={NEW_DATE_FORMAT} />}
        />

        <SummaryPanelRow label={'Currency'} value={order?.invoiceGroup?.currency ?? <NoData />} />
        {renderBillingProfileInfo()}
      </SummaryPanelContainer>
    </div>
  );
};
